import { useState, useCallback, useMemo } from "react";
import {
  Card,
  BlockStack,
  Text,
  Checkbox,
  Button,
  InlineStack,
  Box,
  SkeletonThumbnail,
  InlineGrid,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {
  PlusIcon,
  EditIcon,
  PlusCircleIcon,
  DeleteIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import {
  Event,
  MultilingualText,
  TestimonialItem,
  Testimonials,
  ImageDetailType
} from "@features/events/types";
import AddEditTestimonialModal from "./AddEditTestimonialModal";
import { useParams } from "react-router-dom";
import { GenerativeTextField } from "@shared/ui";
import { getLocalizedText } from "@shared/lib";

// Helper function to get YouTube thumbnail
const getYouTubeThumbnail = (url: string): string | null => {
  const match = url.match(/(?:\?v=|\/embed\/|\.be\/)([^\s&]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
};

interface TestimonialItemComponentProps {
  testimonial: TestimonialItem;
  index: number;
  onEdit: (item: TestimonialItem, index: number) => void;
  onRemove: (index: number) => void;
}

const TestimonialItemComponent: React.FC<TestimonialItemComponentProps> = ({
  testimonial,
  index,
  onEdit,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <SortableResourceItem
      accessibilityLabel={t("eventDetails.testimonials.editItem")}
      dragHandleAccessibilityLabel={`Reorder ${getLocalizedText(testimonial.name) || "testimonial"}`}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlineGrid columns={["twoThirds", "oneThird"]}>
          <InlineStack gap="200" align="start" blockAlign="center">
            {(() => {
              const avatarSrc = testimonial.video
                ? getYouTubeThumbnail(testimonial.video)
                : null;

              return avatarSrc || testimonial.image ? (
                <img
                  src={
                    avatarSrc ||
                    (testimonial.image as ImageDetailType[])[0]?.src ||
                    ""
                  }
                  alt={t("eventDetails.testimonials.mediaAlt")}
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <SkeletonThumbnail size="small" />
              );
            })()}

            <BlockStack gap="100">
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {getLocalizedText(testimonial.name)}
              </Text>
            </BlockStack>
          </InlineStack>

          <InlineStack gap="200" align="space-between" blockAlign="center">
            <Box></Box>
            <Box opacity={isHovered ? "1" : "0"} paddingInlineEnd="100">
              <InlineStack gap="200" blockAlign="center" align="end">
                <Button
                  icon={EditIcon}
                  variant="tertiary"
                  onClick={() => onEdit(testimonial, index)}
                  accessibilityLabel={t("eventDetails.testimonials.editItem")}
                />
                <Button
                  icon={DeleteIcon}
                  tone="critical"
                  variant="tertiary"
                  onClick={() => onRemove(index)}
                  accessibilityLabel={t("eventDetails.testimonials.removeItem")}
                />
              </InlineStack>
            </Box>
          </InlineStack>
        </InlineGrid>
      </div>
    </SortableResourceItem>
  );
};

interface AttendeeTestimonialsCardProps {
  eventData: Partial<Event>;
  onDataChange: (updates: Partial<Event>) => void;
  fieldErrors?: Record<string, string>;
}

export default function AttendeeTestimonialsCard({
  eventData,
  onDataChange,
  fieldErrors = {},
}: AttendeeTestimonialsCardProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TestimonialItem | null>(null);
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();

  // Extract current values from eventData.testimonials
  const testimonials = useMemo(
    () => eventData.testimonials,
    [eventData.testimonials]
  );
  const enableTestimonials = useMemo(
    () => Boolean(testimonials),
    [testimonials]
  );
  const sectionTitle = useMemo(
    () => testimonials?.title,
    [testimonials?.title]
  );
  const sectionDesc = useMemo(
    () => testimonials?.description,
    [testimonials?.description]
  );
  const testimonialItems = useMemo(
    () => testimonials?.testimonials || [],
    [testimonials?.testimonials]
  );

  const updateTestimonials = useCallback(
    (
      updates: Partial<{
        enabled: boolean;
        title: MultilingualText;
        description: MultilingualText;
        testimonials: TestimonialItem[];
      }>
    ) => {
      if (updates.enabled === false) {
        // Remove testimonials when disabled
        onDataChange({ testimonials: undefined });
        return;
      }

      // Create testimonials data
      const testimonialsData: Testimonials = {
        title: updates.title ?? sectionTitle ?? {},
        description: updates.description ?? sectionDesc ?? {},
        testimonials: updates.testimonials ?? testimonialItems,
      };

      onDataChange({ testimonials: testimonialsData });
    },
    [onDataChange, sectionTitle, sectionDesc, testimonialItems]
  );

  const handleToggleTestimonials = (checked: boolean) => {
    updateTestimonials({ enabled: checked });
  };

  const renderTestimonialItem = (
    testimonial: TestimonialItem
  ) => {
    const itemIndex = testimonialItems.indexOf(testimonial);
    return (
      <TestimonialItemComponent
        testimonial={testimonial}
        index={itemIndex}
        onEdit={handleEditTestimonial}
        onRemove={handleRemoveTestimonial}
      />
    );
  };

  const handleAddTestimonial = () => {
    setEditingItem(null);
    setEditingIndex(-1);
    setIsModalOpen(true);
  };

  const handleEditTestimonial = (item: TestimonialItem, index: number) => {
    setEditingItem(item);
    setEditingIndex(index);
    setIsModalOpen(true);
  };

  const handleRemoveTestimonial = (index: number) => {
    const updatedItems = testimonialItems.filter((_, i) => i !== index);
    updateTestimonials({ testimonials: updatedItems });
  };

  const handleSaveTestimonial = (testimonial: TestimonialItem) => {
    if (editingIndex >= 0) {
      // Edit existing item
      const updatedItems = [...testimonialItems];
      updatedItems[editingIndex] = testimonial;
      updateTestimonials({ testimonials: updatedItems });
    } else {
      // Add new item
      const updatedItems = [...testimonialItems, testimonial];
      updateTestimonials({ testimonials: updatedItems });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setEditingIndex(-1);
  };

  const hasTestimonials = testimonialItems.length > 0;

  return (
    <>
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">
            {t("eventDetails.testimonials.title")}
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            {t("eventDetails.testimonials.description")}
          </Text>

          <Checkbox
            label={t("eventDetails.testimonials.enableCheckbox")}
            checked={enableTestimonials}
            onChange={handleToggleTestimonials}
          />

          {enableTestimonials && (
            <BlockStack gap="400">
              <GenerativeTextField
                id="section-title-bg"
                placementId="event.event.section.title"
                label={t("eventDetails.testimonials.sectionTitle")}
                value={sectionTitle ?? {}}
                onChange={(value) => {
                  updateTestimonials({ title: value });
                }}
                canGenerate={true}
                requiredIndicator
                error={fieldErrors["testimonials.title"]}
              />

              <GenerativeTextField
                id="section-description-bg"
                placementId="event.event.section.description"
                label={t("eventDetails.testimonials.sectionDescription")}
                value={sectionDesc ?? {}}
                onChange={(value) => {
                  updateTestimonials({ description: value });
                }}
                type="textArea"
                canGenerate={true}
              />

              {/* Testimonials List */}
              <BlockStack gap="300">
                <Text as="h3" variant="headingSm">
                  {t("eventDetails.testimonials.testimonialsSection")}
                </Text>

                {hasTestimonials ? (
                  <BlockStack gap="200">
                    <Box
                      borderWidth="025"
                      borderColor="border"
                      borderRadius="200"
                      background="bg-surface"
                    >
                      <SortableList
                        items={testimonialItems}
                        onChange={(reorderedTestimonials) => {
                          updateTestimonials({
                            testimonials: reorderedTestimonials,
                          });
                        }}
                        idForItem={(testimonial) =>
                          `testimonial-${testimonialItems.indexOf(
                            testimonial
                          )}-${Object.values(testimonial?.name)[0]}`
                        }
                        renderItem={(item) => renderTestimonialItem(item)}
                        showDividers={true}
                      />
                    </Box>
                    <InlineStack>
                      <Button
                        variant="plain"
                        icon={PlusCircleIcon}
                        onClick={handleAddTestimonial}
                      >
                        {t("eventDetails.testimonials.addButton")}
                      </Button>
                    </InlineStack>
                  </BlockStack>
                ) : (
                  <BlockStack gap="200">
                    <Text
                      as="p"
                      variant="bodyMd"
                      tone="subdued"
                      alignment="center"
                    >
                      {t("eventDetails.testimonials.emptyState")}
                    </Text>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        icon={PlusIcon}
                        onClick={handleAddTestimonial}
                        variant="primary"
                      >
                        {t("eventDetails.testimonials.addButton")}
                      </Button>
                    </div>
                  </BlockStack>
                )}
              </BlockStack>
            </BlockStack>
          )}
        </BlockStack>
      </Card>

      <AddEditTestimonialModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveTestimonial}
        editingItem={editingItem}
        hcc={hcc}
        eventId={eventData.id}
      />
    </>
  );
}
